/*
 * Edit alert Form
 * Add and Edit alerts
 *
 */

import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FieldConfig, FormArray, FormGenerator, FormGroup, Validators } from 'react-reactive-form';
import { useTable, useSortBy, usePagination, Column, useFlexLayout } from 'react-table';
import { FormUtil } from '../../common/FormUtil';
import { IjobHour, Ijob, IlaborRate, INonProductiveHour } from '../../../models';
import { IpartFormValues } from '../../../modelsForms';
import { constants } from '../../../constants/constants';
import { initialJobHour, initialJob } from '../../../reducers/initialState';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { TableUtil } from '../../common/TableUtil';
import { saveNonProductiveHours } from '../../../actions/userActions';
import { TFunction } from 'react-i18next';
import { jobStatusEnum } from '../../../models-enums';

import { v4 as uuidv4 } from 'uuid';

interface Iprops {
    loading: boolean;
    colorButton: string;
    updateFormValue: (formValue: { [key: string]: any }) => any;
    setFormValues: (formValues: { [key: string]: any }) => any;
    t: TFunction;
    formValues: IpartFormValues;
    toggleModal: () => void;
    selectedJobHour: IjobHour;
    laborRates: IlaborRate[];
    disabled?: boolean;
    secondModal?: boolean;
    selectedJob: Ijob;
    currentUserId: string;
    onClose?: () => void;
    tableData: INonProductiveHour[];
    saveNonProductiveHours: typeof saveNonProductiveHours;
}

const AddNPHModalForm: React.FC<Iprops> = props => {
    const {
        t,
        tableData,
        loading,
        colorButton,
        disabled,
        laborRates,
        currentUserId,
        onClose,
        toggleModal,
        setFormValues
    } = props;
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(constants.tablePageSizeDefault);
    const buildFieldConfig = useMemo((): FieldConfig => {
        const isDisabled = disabled === true;
        const laborRatesOptions = laborRates.map((rate: IlaborRate) => {
            return {
                value: rate.id,
                label: `${rate.code} - ${rate.description}`
            };
        });

        const fieldConfigControls = {
            quantity: {
                render: FormUtil.NumericInput,
                meta: {
                    label: 'jobHoursForm.hoursHeader',
                    colWidth: 6,
                    name: 'quantity',
                    min: 0.01,
                    step: 0.01
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim, FormUtil.validators.isValidPositiveFloat]
                },
                formState: { value: 1, disabled: isDisabled }
            },
            date: {
                render: FormUtil.Datetime,
                meta: {
                    label: 'jobHoursForm.dateLabel',
                    colWidth: 6,
                    name: 'date'
                },
                formState: {
                    value: null,
                    disabled: isDisabled
                },
                options: {
                    validators: [FormUtil.validators.isValidMoment, FormUtil.validators.requiredWithTrim]
                }
            },
            description: {
                render: FormUtil.Select,
                meta: {
                    label: 'jobHoursForm.nphLaborRateHeader',
                    options: laborRatesOptions,
                    colWidth: 12,
                    type: 'text',
                    placeholder: 'common:searchPlaceholder',
                    isClearable: true
                },
                options: {
                    validators: [Validators.required]
                },
                formState: { value: null, disabled: isDisabled }
            }
        };
        return FormUtil.translateForm(
            {
                controls: { ...fieldConfigControls }
            },
            t
        );
    }, [disabled, laborRates, t]);

    const [fieldConfig, setFieldConfig] = useState<FieldConfig>(buildFieldConfig);
    const [formGroup, setFormGroup] = useState<FormGroup | any>(null);
    const [subscription, setSubscription] = useState<any>(null);
    const [, forceUpdate] = useState({});

    // Update fieldConfig when buildFieldConfig changes
    useEffect(() => {
        setFieldConfig(buildFieldConfig);
    }, [buildFieldConfig]);

    // Apply blue color styling for NPH table
    const tableClassName = `ReactTable beacon-table -highlight flex-layout nph-table blue`;

    // Build columns using useMemo for performance
    const columns = useMemo(() => {
        return TableUtil.translateHeaders(
            [
                {
                    Header: () => <span>{t('jobHoursForm.nphLaborRateHeader')}</span>,
                    accessor: ({ number, description }: IjobHour) => {
                        return <span>{`${number} - ${description}`}</span>;
                    },
                    width: 260,
                    id: 'laborRate'
                },
                {
                    Header: 'jobHoursForm.hoursHeader',
                    accessor: 'quantity',
                    width: 80
                },
                {
                    Header: 'jobHoursForm.dateHeader',
                    id: 'date',
                    width: 100,
                    accessor: ({ date }: IjobHour) => {
                        return date
                            ? moment
                                  .utc(date)
                                  .local(true)
                                  .format(constants.momentDisplayFormat)
                            : 'n/a';
                    }
                }
            ],
            t
        );
    }, [t]); // Include t dependency for proper translation updates

    // Use react-table v7 hooks with pagination
    const tableInstance = useTable(
        {
            columns: columns as Column<INonProductiveHour>[],
            data: tableData,
            initialState: {
                pageIndex: 0,
                pageSize: constants.tablePageSizeDefault
            },
            manualPagination: false, // Use client-side pagination since data is already loaded
            pageCount: Math.ceil(tableData.length / constants.tablePageSizeDefault)
        } as any,
        useSortBy,
        usePagination,
        useFlexLayout
    ) as any;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize: setTablePageSize,
        state: { pageIndex: tablePageIndex, pageSize: tablePageSize }
    } = tableInstance;

    // Sync pagination state
    useEffect(() => {
        if (tablePageIndex !== pageIndex) {
            setPageIndex(tablePageIndex);
        }
    }, [tablePageIndex, pageIndex]);

    useEffect(() => {
        if (tablePageSize !== pageSize) {
            setPageSize(tablePageSize);
        }
    }, [tablePageSize, pageSize]);

    // since the modal is rendered in this component, this only runs when the ManageInventory view mounts
    useEffect(() => {
        setFieldConfig(buildFieldConfig);
    }, [buildFieldConfig]); // Only depend on buildFieldConfig since it's memoized with proper dependencies

    useEffect(() => {
        // Only rebuild field config if selectedJobHour ID changes
        if (props.selectedJobHour?.id) {
            setFieldConfig(buildFieldConfig);
        }
    }, [props.selectedJobHour?.id, buildFieldConfig]);

    useEffect(() => {
        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
            setFormValues({});
        };
    }, [subscription, setFormValues]); // Include setFormValues dependency

    /*
     * formValuesToSelectedItem - convert the formValues to the shape of the selectedItem
     */
    const formValuesToSelectedItem = () => {
        const formValues = FormUtil.getValues(formGroup.value);

        // formValues.description is the id of the labor rate
        const laborRateNum = laborRates.find(rate => rate.id === formValues.description);
        return {
            ...formValues,
            userId: currentUserId,
            number: laborRateNum?.code,
            laborRateID: laborRateNum?.id,
            description: laborRateNum?.description
        };
    };

    const handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (formGroup.status === 'INVALID') {
            formGroup.markAsSubmitted();
            // Mark all fields as submitted to trigger validation styling
            Object.keys(formGroup.controls).forEach(key => {
                const control = formGroup.get(key);
                if (control) {
                    control.markAsSubmitted();
                }
            });
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            // Force re-render to show validation styling
            forceUpdate({});
            return;
        }
        const cleanValues: any = formValuesToSelectedItem();

        const newProductiveHour: INonProductiveHour = {
            id: uuidv4(),
            ...cleanValues
        };

        props.saveNonProductiveHours(newProductiveHour);
        // Don't rebuild field config here - it will be handled by the useEffect
    };

    const handleDone = () => {
        // If onClose is defined, override close state. Otherwise, set selectedJobHourID to empty string
        if (onClose) {
            onClose();
            return;
        }
        toggleModal();
    };

    const setForm = (form: FormGroup | FormArray) => {
        setFormGroup(form);
        form.meta = {
            loading
        };
        if (subscription) {
            subscription.unsubscribe();
        }
        const newSubscription = form.valueChanges.subscribe(() => {
            // Force re-render to update validation state
            forceUpdate({});
        });
        setSubscription(newSubscription);
    };

    const cleanForm = () => {
        formGroup.reset();
    };

    let isQuantityInvalid = true;
    if (formGroup && formGroup.get('quantity')) {
        const quantityField = formGroup.get('quantity');
        isQuantityInvalid = quantityField.invalid;
    }

    return (
        <form onSubmit={handleSubmit} className="clearfix beacon-form">
            <Col xs={12} className="py-2">
                <Row>
                    <FormGenerator onUnmount={cleanForm} onMount={setForm} fieldConfig={fieldConfig} />
                </Row>
            </Col>

            <Row>
                <Col xs={12}>
                    <div className="nph-form">
                        <div {...getTableProps()} className={tableClassName}>
                            <div className="rt-thead -header">
                                {headerGroups.map((headerGroup: any) => (
                                    <div
                                        {...headerGroup.getHeaderGroupProps()}
                                        key={headerGroup.getHeaderGroupProps().key}
                                        className="rt-tr"
                                    >
                                        {headerGroup.headers.map((column: any) => (
                                            <div
                                                {...column.getHeaderProps()}
                                                key={column.getHeaderProps().key}
                                                className="rt-th"
                                            >
                                                <div>{column.render('Header')}</div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <div {...getTableBodyProps()} className="rt-tbody">
                                {page.map((row: any, i: number) => {
                                    prepareRow(row);
                                    return (
                                        <div
                                            {...row.getRowProps()}
                                            key={row.getRowProps().key}
                                            className={`rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`}
                                        >
                                            {row.cells.map((cell: any) => {
                                                return (
                                                    <div
                                                        {...cell.getCellProps()}
                                                        key={cell.getCellProps().key}
                                                        className="rt-td"
                                                    >
                                                        {cell.render('Cell')}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {tableData.length > 0 && (
                            <div className="table-pagination">
                                <Button
                                    onClick={() => {
                                        previousPage();
                                    }}
                                    variant="outline"
                                    className="prev"
                                    disabled={!canPreviousPage}
                                >
                                    {t('common:previous')}
                                </Button>

                                <div className="controls">
                                    <label htmlFor="page-input">{t('common:pageText')} </label>
                                    <span>
                                        <input
                                            id="page-input"
                                            type="number"
                                            min={1}
                                            max={pageCount}
                                            className="page-input"
                                            defaultValue={pageIndex + 1}
                                            key={`page-input-${pageIndex}`}
                                            disabled={loading}
                                            onChange={e => {
                                                const gotoPageNum = e.target.value ? Number(e.target.value) - 1 : 0;
                                                if (gotoPageNum >= 0 && gotoPageNum < pageCount) {
                                                    gotoPage(gotoPageNum);
                                                }
                                            }}
                                            onWheel={e => e.currentTarget.blur()}
                                        />
                                    </span>
                                    <span>{t('common:of')} </span>
                                    <span>{pageCount}</span>

                                    <select
                                        value={pageSize}
                                        className="table-page-size-select"
                                        onChange={e => {
                                            setTablePageSize(Number(e.target.value));
                                        }}
                                    >
                                        {constants.tablePageSizeOptions.map(size => (
                                            <option key={size} value={size}>
                                                {t('common:show')} {size}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <Button
                                    onClick={() => {
                                        nextPage();
                                    }}
                                    variant="outline"
                                    className="next"
                                    disabled={!canNextPage}
                                >
                                    {t('common:next')}
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Col xs={12} className="form-buttons d-flex justify-content-between gap-2 mt-0">
                <Button variant="default" type="button" onClick={handleDone}>
                    {String(t('common:doneButton'))}
                </Button>
                <Button
                    variant="default"
                    type="button"
                    className="pull-left"
                    style={{
                        marginLeft: '20px',
                        display: props.selectedJobHour.id.length ? '' : 'none'
                    }}
                    onClick={() => {
                        if (onClose) {
                            onClose();
                        }
                    }}
                >
                    {String(t('common:cancel'))}
                </Button>
                <Button variant={colorButton} type="submit" disabled={isQuantityInvalid}>
                    {props.selectedJob.status === jobStatusEnum.reopened
                        ? String(t('common:save'))
                        : String(t('jobHoursForm.saveSendToOfficeButton'))}
                </Button>
            </Col>
        </form>
    );
};

AddNPHModalForm.defaultProps = {
    selectedJobHour: initialJobHour,
    colorButton: constants.colors[`blueButton`],
    selectedJob: initialJob
};

export default AddNPHModalForm;
